import { clerkApiInterceptor } from '../authenticatedApi/clerkApiInterceptor';
import {
	CloneWidgetResponse,
	CustomWidgetDbEntity,
	DeleteWidgetResponse,
	GetPublicWidgetInfoResponse,
	SaveCustomWidgetRequest,
	SaveCustomWidgetResponse,
	ShareWidgetResponse,
	UpdateWidgetRequest,
	UpdateWidgetResponse
} from '@kemu-io/kemu-types';
import globals from '../../common/globals';

const API_NAME = globals.PLATFORM_API_NAME;

// Forces to return the entire Axios response object instead of only response.data
const returnAxiosResponse = { response: true };

/**
 * Creates a new widget and associate it to the current user profile.
 * It returns an object with an 'uploadUrl' property that must be used to upload the
 * actual contents to storage.
 * @param widgetInfo 
 */
const saveNewWidget = async (widgetInfo: SaveCustomWidgetRequest): Promise<SaveCustomWidgetResponse> => {
	// POST /widget
	const response = await clerkApiInterceptor.post(API_NAME, `/v1/widget`, { ...returnAxiosResponse, body: widgetInfo });
	const data = response.data as SaveCustomWidgetResponse;
	return data;
};

/**
 * Gets in formation about a single widget. The 'contents' property will contain
 * a pre-signed url to download the actual contents. 
 * IMPORTANT: Don't download the widget contents, instead use the `importPublicWidget` api call 
 * to import the widget into the user's collection.
 * 
 * @param publicId the id of a publicly shared widget.
 */
const getWidgetInfoByPublicId = async (publicId: string): Promise<GetPublicWidgetInfoResponse> => {
	const response = await clerkApiInterceptor.get(API_NAME, `/v1/widget/public/${publicId}`, returnAxiosResponse);
	const data = response.data as GetPublicWidgetInfoResponse;
	return data;
};


/**
 * Updates an existing widget
 */
const updateWidget = async (config: {
	widgetId: string;
	widgetInfo: UpdateWidgetRequest;
}): Promise<UpdateWidgetResponse> => {
	const response = await clerkApiInterceptor.put(API_NAME, `/v1/widget/${config.widgetId}`, { ...returnAxiosResponse, body: config.widgetInfo });
	const data = response.data as UpdateWidgetResponse;
	return data;
};

/**
 * Deletes an existing widget
 */
const deleteWidget = async (widgetId: string): Promise<DeleteWidgetResponse> => {
	const response = await clerkApiInterceptor.delete(API_NAME, `/v1/widget/${widgetId}`, returnAxiosResponse);
	const data = response.data as DeleteWidgetResponse;
	return data;
};

/**
 * Enables/Disables link sharing
 */
const toggleLinkSharing = async (widgetId: string, enable: boolean): Promise<ShareWidgetResponse> => {
	// POST /widget/{widgetId}/linkShare/{action: 'enable' | 'disable'}
	const response = await clerkApiInterceptor.post(API_NAME, `/widget/${widgetId}/linkShare/${enable ? 'enable' : 'disable'}`, returnAxiosResponse);
	const data = response.data as ShareWidgetResponse;
	return data;
};

// /**
//  * Creates a copy of the given widget and changes the name.
//  * @param widgetId the id of widget entity to be duplicated. This is used so that any link tutorial is preserved
//  * @param widgetInfo extra information about the info used to replace the current metadata in the cloned entity.
//  */
// const saveWidgetAs = async (widgetId: string, widgetInfo: SaveAsWidgetRequest): Promise<SaveAsWidgetResponse> => {
// 	// POST /widget/{widgetId}/saveAs
// 	const response = await clerkApiInterceptor.post(API_NAME, `/v1/widget/${widgetId}/saveAs`, { ...returnAxiosResponse, body: { ...widgetInfo } });
// 	const data = response.data as SaveAsWidgetResponse;
// 	return data;
// };

/**
 * Adds a public widget to the user account
 */
const importPublicWidget = async (publicId: string): Promise<CustomWidgetDbEntity> => {
	// POST /widget/{widgetId}/saveAs
	const response = await clerkApiInterceptor.post(API_NAME, `/widget/${publicId}/import`, returnAxiosResponse);
	const data = response.data as CloneWidgetResponse;
	return data.widget;
};

export default {
	getWidgetInfoByPublicId,
	updateWidget,
	saveNewWidget,
	// saveWidgetAs,
	importPublicWidget,
	deleteWidget,
	toggleLinkSharing,
};
